import styled from "styled-components";
import Slider from "react-slick";
import { useTranslation } from "react-i18next";
import { TApplicationData } from "@/typings/Props";
import Header from "@/components/main/Header";
import Banner from "@/components/learn/Banner";
import Items from "@/components/learn/Items";
import YoutubeSlick, { type TYoutubeItem } from "@/components/learn/YoutubeSlick";
import OtherModels from "@/components/products/detail/OtherModels";
import ContactUs from "@/components/contact-us";
import Footer from "@/components/main/Footer";

const YoutubeSlickWrap = styled.div`
  background-color: #fff;
  margin: 50px 0;
  padding: 50px 0;
`;

const CustomersWrap = styled.div`
  padding: 50px 20px;
`;

const CustomersTitle = styled.p`
  font-size: 2.6rem;
  font-weight: 700;
  text-align: center;
  margin: 30px;
`;
const CustomersItemWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 20px;
  @media (max-width: 1300px) {
    flex-wrap: wrap;
  }
`;
const CustomersItem = styled.div`
  width: 465px;
  height: 600px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  @media (max-width: 1300px) {
    width: 45%;
  }
  @media (max-width: 1000px) {
    width: 90%;
  }
`;

const ItemImgArea = styled.div<{ $bg: string }>`
  width: 100%;
  height: 50%;
  background: url(${({ $bg }) => $bg});
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: 20px;
  background-position: 0px -25px;
  @media (max-width: 1000px) {
    background-size: contain;
    background-position: center;
  }
`;
const ItemTextArea = styled.div`
  width: 100%;
  height: 50%;
  background-color: white;
  border-radius: 20px;
`;

const P = styled.p`
  text-align: center;
  color: #444444;
`;
const ItemStep = styled(P)`
  margin-top: 20px;
  color: #971b2f;
  font-size: 2rem;
  font-weight: 700;
  text-align: center;
`;
const ItemTitle = styled(P)`
  padding: 13px;
  font-size: 1.5rem;
  font-weight: 700;
`;
const ItemDesc = styled(P)`
  line-height: 150%;
  padding: 0 15px;
`;

const HealthCornerWrap = styled.div`
  width: 100%;
  padding: 60px 0;
  background-color: white;
`;
const HealthCornerTitle = styled(CustomersTitle)`
  color: #444444;
  margin: 10px;
`;
const HealthCornerDesc = styled(P)`
  font-size: 1.5rem;
  font-weight: 400;
  margin-bottom: 50px;
`;
const HealthCornerBg = styled.div<{ $bg: string }>`
  position: relative;
  width: 100%;
  height: 600px;
  background: url(${({ $bg }) => $bg});
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;

  @media (max-width: 1000px) {
    height: 300px;
  }
`;
const StyledSlider = styled(Slider)`
  position: relative;
  .slick-prev::before,
  .slick-next::before {
    opacity: 0;
    display: none;
  }
  .slick-slide div {
    cursor: pointer;
  }
`;

const ArrowBtn = styled.button<{ isNext?: boolean }>`
  position: absolute;
  color: white;
  font-weight: 700;
  z-index: 9999;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background-color: #971b2f;
  ${({ isNext }) => (isNext ? "right: 500px; top: 50%;" : "left: 500px; top: 50%;")}
  transition: 0.2s ease-out;
  &:hover {
    background-color: #971b2f;
    transition: 0.2s ease-out;
    width: 45px;
    height: 45px;
  }
  @media (max-width: 1600px) {
    display: none;
  }
`;
const NextArrow = (props: { onClick?: () => void }) => {
  const { onClick } = props;
  return (
    <ArrowBtn isNext onClick={onClick}>
      →
    </ArrowBtn>
  );
};

const PrevArrow = (props: { onClick?: () => void }) => {
  const { onClick } = props;
  return <ArrowBtn onClick={onClick}>←</ArrowBtn>;
};

const ScreeningEventWrap = styled.div`
  width: 100%;
  padding: 60px 0;
`;
const ScreeningEventItemWrap = styled.div`
  width: 95%;
  margin: auto;
  display: flex;
  flex-wrap: wrap;
  margin-top: 70px;
  gap: 50px;
  justify-content: center;
`;
const ScreeningEventItem = styled.div`
  width: 423px;
  height: 453px;
  background-color: white;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  align-itmes: center;
`;
const ScreeningEventImg = styled.div<{ $height: string; $bg: string }>`
  width: 100%;
  height: ${({ $height }) => $height};
  border-radius: 10px;
  background: url(${({ $bg }) => $bg});
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
`;

export default function Home() {
  const { t } = useTranslation();
  const items: TApplicationData[] = [
    {
      id: 1,
      title: t("menu.learn.pharmacy.item.title-1"),
      desc: t("menu.learn.pharmacy.item.desc-1"),
      iconPath: "/learn/application/Raise-Community-Health-Awareness.svg",
    },
    {
      id: 2,
      title: t("menu.learn.pharmacy.item.title-2"),
      desc: t("menu.learn.pharmacy.item.desc-2"),
      iconPath: "/learn/application/Customer-Retention.svg",
    },
    {
      id: 3,
      title: t("menu.learn.pharmacy.item.title-3"),
      desc: t("menu.learn.pharmacy.item.desc-3"),
      iconPath: "/learn/application/Customer-Focused-Competitive-Advantage.svg",
    },
    {
      id: 4,
      title: t("menu.learn.pharmacy.item.title-4"),
      desc: t("menu.learn.pharmacy.item.desc-4"),
      iconPath: "/learn/application/Additional-Revenue.svg",
    },
  ];

  const youtubeItems: TYoutubeItem[] = [
    {
      title: t("menu.learn.pharmacy.youtube-item-1.title"),
      src: "https://www.youtube.com/embed/wuwz-dqe5pk?feature=oembed&enablejsapi=1&origin=https://inbodyasia.com",
      descSubTitle: t("menu.learn.pharmacy.youtube-item-1.desc.sub-title"),
      descTitle: t("menu.learn.pharmacy.youtube-item-1.desc.title"),
      desc: t("menu.learn.pharmacy.youtube-item-1.desc.desc"),
    },
    {
      title: t("menu.learn.pharmacy.youtube-item-2.title"),
      src: "https://www.youtube.com/embed/S_cJtnnT4to?feature=oembed&enablejsapi=1&origin=https://inbodyasia.com",
      descSubTitle: t("menu.learn.pharmacy.youtube-item-2.desc.sub-title"),
      descTitle: t("menu.learn.pharmacy.youtube-item-2.desc.title"),
      desc: t("menu.learn.pharmacy.youtube-item-2.desc.desc"),
    },
  ];

  const customerItems: TApplicationData[] = [
    {
      id: 1,
      title: t("menu.learn.pharmacy.customer.item.title-1"),
      desc: t("menu.learn.pharmacy.customer.item.desc-1"),
      iconPath: "/learn/application/pharmacy/Family-Visit-Pharmacy.png",
    },
    {
      id: 2,
      title: t("menu.learn.pharmacy.customer.item.title-2"),
      desc: t("menu.learn.pharmacy.customer.item.desc-2"),
      iconPath: "/learn/application/pharmacy/Doing-InBody.png",
    },
    {
      id: 3,
      title: t("menu.learn.pharmacy.customer.item.title-3"),
      desc: t("menu.learn.pharmacy.customer.item.desc-3"),
      iconPath: "/learn/application/pharmacy/Consultation.png",
    },
    {
      id: 4,
      title: t("menu.learn.pharmacy.customer.item.title-4"),
      desc: t("menu.learn.pharmacy.customer.item.desc-4"),
      iconPath: "/learn/application/pharmacy/Recommend-Supplements.png",
    },
  ];
  const healthCornerItems: { id: number; img: string }[] = [
    {
      id: 1,
      img: "/learn/application/pharmacy/Pharmacy-Setup-1-2048x1536.webp",
    },
    {
      id: 2,
      img: "/learn/application/pharmacy/Pharmacy-Setup-2-2048x1536.webp",
    },
  ];
  const screeningEventItems: { id: number; logo: string; img: string }[] = [
    {
      id: 1,
      logo: "/learn/application/pharmacy/Well_logo.png",
      img: "/learn/application/pharmacy/Well-Pharmacy-2048x1536.jpg",
    },
    {
      id: 2,
      logo: "/learn/application/pharmacy/Best_logo.png",
      img: "/learn/application/pharmacy/Best-Pharmacy-Sibu-2048x1536.jpg",
    },
    {
      id: 3,
      logo: "/learn/application/pharmacy/healthlane-logo-removebg-preview.png",
      img: "/learn/application/pharmacy/Health-Lane-2048x1536.jpg",
    },
    {
      id: 4,
      logo: "/learn/application/pharmacy/Farmasi-Chong_logo.png",
      img: "/learn/application/pharmacy/Farmasi-Chong-2048x1536.jpg",
    },
    {
      id: 5,
      logo: "/learn/application/pharmacy/Sunlight_logo.png",
      img: "/learn/application/pharmacy/Sunlight-Pharmacy-2048x1536.jpg",
    },
    {
      id: 6,
      logo: "/learn/application/pharmacy/AM-PM_logo.png",
      img: "/learn/application/pharmacy/AM-PM-Pharmacy-2048x1536.jpg",
    },
    {
      id: 7,
      logo: "/learn/application/pharmacy/KDH_logo.png",
      img: "/learn/application/pharmacy/KDH-Pharmacy-2048x1536.jpg",
    },
  ];
  return (
    <>
      <Header />
      <Banner
        Bg="/learn/banner/Banner.png"
        Headline={t("menu.learn.banner.headline")}
        Title={t("menu.learn.pharmacy.banner.title")}
        Desc={t("menu.learn.pharmacy.banner.desc")}
      />
      <Items title={t("menu.learn.pharmacy.item.main-title")} items={items} />
      <CustomersWrap>
        <CustomersTitle data-aos="fade-up" data-aos-easing="ease-out" data-aos-delay="200" data-aos-duration="500">
          {t("menu.learn.pharmacy.customer")}
        </CustomersTitle>
        <CustomersItemWrap>
          {customerItems.map((data) => {
            return (
              <CustomersItem key={data.id}>
                <ItemImgArea $bg={data.iconPath}></ItemImgArea>
                <ItemTextArea>
                  <ItemStep>
                    {t("menu.learn.pharmacy.customer.step")} {data.id}
                  </ItemStep>
                  <ItemTitle>{data.title}</ItemTitle>
                  <ItemDesc>{data.desc}</ItemDesc>
                </ItemTextArea>
              </CustomersItem>
            );
          })}
        </CustomersItemWrap>
      </CustomersWrap>
      <YoutubeSlickWrap>
        <YoutubeSlick youtubeItems={youtubeItems} />
      </YoutubeSlickWrap>
      <HealthCornerWrap>
        <HealthCornerTitle
          dangerouslySetInnerHTML={{ __html: t("menu.learn.pharmacy.health-corner.title") }}
        ></HealthCornerTitle>
        <HealthCornerDesc>{t("menu.learn.pharmacy.health-corner.desc")}</HealthCornerDesc>
        <StyledSlider
          {...{
            dots: true,
            autoplay: false,
            nextArrow: <NextArrow />,
            prevArrow: <PrevArrow />,
          }}
        >
          {healthCornerItems.map((data) => {
            return <HealthCornerBg key={data.id} $bg={data.img} />;
          })}
        </StyledSlider>
      </HealthCornerWrap>
      <ScreeningEventWrap>
        <HealthCornerTitle>{t("menu.learn.pharmacy.health-screnning.title")}</HealthCornerTitle>
        <ScreeningEventItemWrap>
          {screeningEventItems.map((data) => {
            return (
              <ScreeningEventItem key={data.id}>
                <ScreeningEventImg $height="35%" $bg={data.logo}></ScreeningEventImg>
                <ScreeningEventImg $height="65%" $bg={data.img}></ScreeningEventImg>
              </ScreeningEventItem>
            );
          })}
        </ScreeningEventItemWrap>
      </ScreeningEventWrap>
      <OtherModels />
      <div style={{ margin: "50px 0 0 0" }} />
      <ContactUs />
      <Footer />
    </>
  );
}
